<template>
	<div class="directions-panel" :class="{ 'is-expanded': isExpanded }">
		
		<div v-if="!isExpanded" class="directions-toggle" @click="togglePanel">
			<WFIcon name="path"></WFIcon>
			<WFTranslate k="directions">Reittiohjeet</WFTranslate>
		</div>

		
		<div v-else class="directions-content">
			<div class="directions-header">
				<span class="header-title"><WFTranslate k="directions">Reittiohjeet</WFTranslate></span>
				<button class="close-btn" @click="togglePanel">×</button>
			</div>

			<div class="directions-body">
				
				<div class="location-input">
					<div class="input-icon start-icon">
						<div class="icon-dot start-dot"></div>
					</div>
					<div class="input-wrapper">
						<input
							type="text"
							v-model="startSearch"
							@input="searchStart"
							@focus="setActiveInput('start')"
							:placeholder="startPlaceholder"
							class="location-field"
						/>
						<button v-if="startPOI" class="clear-btn" @click="clearStart">×</button>
						
						<div v-if="startResults.length > 0 && activeInput === 'start'" class="search-results">
							<div
								v-for="poi in startResults"
								:key="'start-' + poi.id"
								class="search-result-item"
								@mousedown.prevent="selectStartPOI(poi)"
							>
								<img v-if="poi.getIconUrl && poi.getIconUrl()" :src="poi.getIconUrl()" class="result-icon" />
								<div class="result-info">
									<span class="result-name">{{ getPOIName(poi) }}</span>
									<span class="result-floor" v-if="getFloorName(poi)">{{ getFloorName(poi) }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<button class="swap-btn" @click="swapLocations" :title="swapTitle">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
					</svg>
				</button>

				<div class="location-input">
					<div class="input-icon end-icon">
						<div class="icon-dot end-dot"></div>
					</div>
					<div class="input-wrapper">
						<input
							type="text"
							v-model="endSearch"
							@input="searchEnd"
							@focus="setActiveInput('end')"
							:placeholder="endPlaceholder"
							class="location-field"
						/>
						<button v-if="endPOI" class="clear-btn" @click="clearEnd">×</button>
						<div v-if="endResults.length > 0 && activeInput === 'end'" class="search-results">
							<div
								v-for="poi in endResults"
								:key="'end-' + poi.id"
								class="search-result-item"
								@mousedown.prevent="selectEndPOI(poi)"
							>
								<img v-if="poi.getIconUrl && poi.getIconUrl()" :src="poi.getIconUrl()" class="result-icon" />
								<div class="result-info">
									<span class="result-name">{{ getPOIName(poi) }}</span>
									<span class="result-floor" v-if="getFloorName(poi)">{{ getFloorName(poi) }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="directions-footer">
				<button 
					class="show-route-btn btn btn-active"
					:disabled="!canShowRoute"
					@click="showRoute"
				>
					<WFIcon name="path"></WFIcon>
					<label><WFTranslate k="show_route">Näytä reitti</WFTranslate></label>
				</button>
				<button 
					v-if="hasActiveRoute"
					class="clear-route-btn btn btn-border"
					@click="clearRoute"
				>
					<WFTranslate k="clear">Tyhjennä</WFTranslate>
				</button>
			</div>

			<div class="directions-keyboard" v-if="activeInput && !isMobile">
				<div class="keyboard-row">
					<button v-for="key in keyboardRow1" :key="'k1-'+key" class="keyboard-key" @mousedown.prevent="typeKey(key)">{{ key }}</button>
				</div>
				<div class="keyboard-row">
					<button v-for="key in keyboardRow2" :key="'k2-'+key" class="keyboard-key" @mousedown.prevent="typeKey(key)">{{ key }}</button>
				</div>
				<div class="keyboard-row">
					<button v-for="key in keyboardRow3" :key="'k3-'+key" class="keyboard-key" @mousedown.prevent="typeKey(key)">{{ key }}</button>
				</div>
				<div class="keyboard-row keyboard-row-bottom">
					<button class="keyboard-key key-backspace" @mousedown.prevent="backspace">⌫</button>
					<button class="keyboard-key key-space" @mousedown.prevent="typeKey(' ')"></button>
					<button class="keyboard-key key-clear" @mousedown.prevent="clearInput">✕</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { WFTranslate } from '@3dwayfinder/wayfinder-vue-components';

export default {
	name: 'DirectionsPanel',
	components: {
		WFTranslate
	},
	data() {
		return {
			isExpanded: false,
			startPOI: null,
			endPOI: null,
			startSearch: '',
			endSearch: '',
			startResults: [],
			endResults: [],
			activeInput: null,
			avoidStairs: false,
			hasActiveRoute: false,
			originalKiosk: null,
			// Keyboard layouts - Finnish/Swedish compatible
			keyboardRow1: ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Å'],
			keyboardRow2: ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
			keyboardRow3: ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
		};
	},
	computed: {
		...mapState('wf', ['pois', 'language', 'floors']),
		
		canShowRoute() {
			return this.startPOI && this.endPOI;
		},
		
		isMobile() {
			// Check if device is mobile (has touch and small screen)
			if (typeof window === 'undefined') return false;
			const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const isSmallScreen = window.innerWidth <= 1024 && window.innerHeight <= 1024;
			const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
			return isMobileUA && hasTouch && isSmallScreen;
		},
		
		startPlaceholder() {
			const placeholders = {
				'fi': 'Valitse lähtöpiste...',
				'en': 'Select start point...',
				'sv': 'Välj startpunkt...'
			};
			return placeholders[this.language] || placeholders['fi'];
		},
		
		endPlaceholder() {
			const placeholders = {
				'fi': 'Valitse määränpää...',
				'en': 'Select destination...',
				'sv': 'Välj destination...'
			};
			return placeholders[this.language] || placeholders['fi'];
		},
		
		swapTitle() {
			const titles = {
				'fi': 'Vaihda sijainteja',
				'en': 'Swap locations',
				'sv': 'Byt platser'
			};
			return titles[this.language] || titles['fi'];
		}
	},
	methods: {
		togglePanel() {
			this.isExpanded = !this.isExpanded;
			if (!this.isExpanded) {
				this.startResults = [];
				this.endResults = [];
				this.activeInput = null;
			}
		},
		
		setActiveInput(input) {
			this.activeInput = input;
			// Clear the other results when focusing
			if (input === 'start') {
				this.endResults = [];
			} else {
				this.startResults = [];
			}
		},
		
		searchPOIs(query) {
			if (!query || query.length < 1) {
				return [];
			}
			
			const searchTerm = query.toLowerCase();
			const lang = this.language || 'fi';
			
			return this.pois
				.filter(poi => {
					if (!poi || !poi.getShowInMenu || !poi.getShowInMenu()) return false;
					const name = poi.getName ? poi.getName(lang) : '';
					return name && name.toLowerCase().includes(searchTerm);
				})
				.slice(0, 8);
		},
		
		searchStart() {
			this.startPOI = null;
			this.startResults = this.searchPOIs(this.startSearch);
		},
		
		searchEnd() {
			this.endPOI = null;
			this.endResults = this.searchPOIs(this.endSearch);
		},
		
		selectStartPOI(poi) {
			this.startPOI = poi;
			this.startSearch = this.getPOIName(poi);
			this.startResults = [];
			this.activeInput = null;
		},
		
		selectEndPOI(poi) {
			this.endPOI = poi;
			this.endSearch = this.getPOIName(poi);
			this.endResults = [];
			this.activeInput = null;
		},
		
		clearStart() {
			this.startPOI = null;
			this.startSearch = '';
			this.startResults = [];
			if (this.hasActiveRoute) {
				this.clearRoute();
			}
		},
		
		clearEnd() {
			this.endPOI = null;
			this.endSearch = '';
			this.endResults = [];
			if (this.hasActiveRoute) {
				this.clearRoute();
			}
		},
		
		swapLocations() {
			const tempPOI = this.startPOI;
			const tempSearch = this.startSearch;
			
			this.startPOI = this.endPOI;
			this.startSearch = this.endSearch;
			this.endPOI = tempPOI;
			this.endSearch = tempSearch;
			
			// Clear any search results
			this.startResults = [];
			this.endResults = [];
			
			// If there's an active route, recalculate it
			if (this.hasActiveRoute && this.canShowRoute) {
				this.showRoute();
			}
		},
		
		getPOIName(poi) {
			if (!poi) return '';
			const lang = this.language || 'fi';
			return poi.getName ? poi.getName(lang) : '';
		},
		
		getFloorName(poi) {
			if (!poi || !poi.getFloor) return '';
			const floor = poi.getFloor();
			if (!floor) return '';
			const lang = this.language || 'fi';
			return floor.getName ? floor.getName(lang) : '';
		},
		
		showRoute() {
			if (!this.canShowRoute) return;
			
			const startName = this.getPOIName(this.startPOI);
			const endName = this.getPOIName(this.endPOI);
			console.log('📍 Showing route from', startName, 'to', endName);
			
			try {
				const wf = this.$wayfinder;
				if (!wf) {
					console.error('Wayfinder not available');
					return;
				}
				
				// Clear any existing path
				if (wf.clearPath) {
					wf.clearPath();
				}
				
				// Get nodes for pathfinding
				const startNode = this.startPOI.getNode ? this.startPOI.getNode() : null;
				const endNode = this.endPOI.getNode ? this.endPOI.getNode() : null;
				
				if (!startNode || !endNode) {
					console.error('Could not get nodes for POIs');
					return;
				}
				
		
				if (wf.options) {
					wf.options.avoidStairs = this.avoidStairs;
					wf.options.accessibleRoute = this.avoidStairs;
				}
				
			
				if (wf.options && wf.options.kiosk) {
					this.originalKiosk = wf.options.kiosk;
				}
				
				// TRICK: Temporarily set start POI's node as the "kiosk"
				// This makes showPath() calculate from startPOI instead of actual kiosk
				if (wf.options) {
					wf.options.kiosk = startNode.id;
				}
				
			
				if (wf.showPath) {
					wf.showPath(endNode, this.endPOI);
				}
				
				
				setTimeout(() => {
					if (wf.options && this.originalKiosk) {
						wf.options.kiosk = this.originalKiosk;
					}
				}, 500);
				
				
				if (wf.clearHighlights) {
					wf.clearHighlights();
				}
				if (wf.setHighlights) {
					wf.setHighlights([this.startPOI, this.endPOI]);
				}
				
				
				if (wf.showFloor) {
					const startFloor = this.startPOI.getFloor ? this.startPOI.getFloor() : null;
					if (startFloor) {
						wf.showFloor(startFloor);
					}
				}
				
				this.hasActiveRoute = true;
				
				this.$emit('routeShown', {
					start: this.startPOI,
					end: this.endPOI
				});
				
				console.log('🗺️ Route shown successfully!');
			} catch (error) {
				console.error('Error showing route:', error);
			}
		},
		
		clearRoute() {
			try {
				const wf = this.$wayfinder;
				
				if (wf) {
					if (wf.clearPath) {
						wf.clearPath();
					}
					if (wf.clearHighlights) {
						wf.clearHighlights();
					}
					
				
					if (wf.options && this.originalKiosk) {
						wf.options.kiosk = this.originalKiosk;
					}
				}
				
				this.hasActiveRoute = false;
				this.$emit('routeCleared');
			} catch (error) {
				console.error('Error clearing route:', error);
			}
		},
		
		resetPanel() {
			this.clearRoute();
			this.startPOI = null;
			this.endPOI = null;
			this.startSearch = '';
			this.endSearch = '';
			this.startResults = [];
			this.endResults = [];
			this.activeInput = null;
			this.isExpanded = false;
		},
		
	
		typeKey(key) {
			if (this.activeInput === 'start') {
				this.startSearch += key;
				this.searchStart();
			} else if (this.activeInput === 'end') {
				this.endSearch += key;
				this.searchEnd();
			}
		},
		
		backspace() {
			if (this.activeInput === 'start' && this.startSearch.length > 0) {
				this.startSearch = this.startSearch.slice(0, -1);
				this.searchStart();
			} else if (this.activeInput === 'end' && this.endSearch.length > 0) {
				this.endSearch = this.endSearch.slice(0, -1);
				this.searchEnd();
			}
		},
		
		clearInput() {
			if (this.activeInput === 'start') {
				this.startSearch = '';
				this.startResults = [];
				this.startPOI = null;
			} else if (this.activeInput === 'end') {
				this.endSearch = '';
				this.endResults = [];
				this.endPOI = null;
			}
		}
	}
};
</script>

<style lang="less">
@import '../css/variables';

.directions-panel {
	position: absolute;
	top: 2rem;
	left: 2rem;
	z-index: 100;
	font-size: 1.6rem;
}


.directions-toggle {
	display: flex;
	align-items: center;
	gap: 0.8rem;
	background: white;
	padding: 1.4rem 2rem;
	border-radius: @border-radius;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
	cursor: pointer;
	transition: all 0.2s ease;
	border: 2px solid var(--th-primary);
	
	&:hover {
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	}
	
	.wf-icon {
		width: 2.4rem;
		height: 2.4rem;
		fill: var(--th-primary);
		--icon_color: var(--th-primary);
	}
	
	span, label {
		font-weight: 600;
		color: #333;
		font-size: 1.6rem;
	}
}


.directions-content {
	background: white;
	border-radius: @border-radius;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	width: 36rem;
	overflow: hidden;
}

.directions-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1.6rem 2rem;
	background: var(--th-primary);
	color: var(--th-active-content);
	
	.header-title {
		font-weight: 700;
		font-size: 1.8rem;
	}
	
	.close-btn {
		background: none;
		border: none;
		color: var(--th-active-content);
		font-size: 2.4rem;
		cursor: pointer;
		width: 3rem;
		height: 3rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		transition: background 0.2s;
		
		&:hover {
			background: rgba(255, 255, 255, 0.2);
		}
	}
}

.directions-body {
	padding: 2rem;
	position: relative;
}


.location-input {
	display: flex;
	align-items: center;
	margin-bottom: 1.2rem;
	
	&:last-of-type {
		margin-bottom: 0;
	}
}

.input-icon {
	width: 3rem;
	display: flex;
	justify-content: center;
	flex-shrink: 0;
}

.icon-dot {
	width: 1.2rem;
	height: 1.2rem;
	border-radius: 50%;
	
	&.start-dot {
		background: var(--th-primary);
	}
	
	&.end-dot {
		background: #000000;
	}
}

.location-field {
	width: 100%;
	padding: 1.2rem 3.5rem 1.2rem 1.4rem;
	border: 2px solid #E0E0E0;
	border-radius: 0.6rem;
	font-size: 1.4rem;
	transition: border-color 0.2s;
	
	&:focus {
		outline: none;
		border-color: var(--th-primary);
	}
	
	&::placeholder {
		color: #999;
	}
}

.clear-btn {
	position: absolute;
	right: 0.8rem;
	top: 50%;
	transform: translateY(-50%);
	background: #ccc;
	border: none;
	width: 2rem;
	height: 2rem;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.4rem;
	
	&:hover {
		background: #999;
	}
}

/* Swap button */
.swap-btn {
	position: absolute;
	right: 2rem;
	top: 5rem;
	background: white;
	border: 2px solid #E0E0E0;
	width: 3.6rem;
	height: 3.6rem;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	z-index: 1;
	
	svg {
		width: 1.8rem;
		height: 1.8rem;
		stroke: #666;
	}
	
	&:hover {
		border-color: var(--th-primary);
		background: #f5f5f5;
		
		svg {
			stroke: var(--th-primary);
		}
	}
}

/* Search results dropdown - now relative to input wrapper */
.input-wrapper {
	flex: 1;
	position: relative;
}

.search-results {
	position: absolute;
	left: 0;
	right: 0;
	top: 100%;
	margin-top: 0.4rem;
	background: white;
	border: 1px solid #E0E0E0;
	border-radius: 0.6rem;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	max-height: 12rem;
	overflow-y: auto;
	z-index: 100;
}

.search-result-item {
	display: flex;
	align-items: center;
	padding: 1.2rem 1.4rem;
	cursor: pointer;
	transition: background 0.15s;
	border-bottom: 1px solid #f0f0f0;
	
	&:last-child {
		border-bottom: none;
	}
	
	&:hover {
		background: #f5f5f5;
	}
}

.result-icon {
	width: 3.2rem;
	height: 3.2rem;
	object-fit: contain;
	margin-right: 1.2rem;
	flex-shrink: 0;
}

.result-info {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.result-name {
	font-weight: 500;
	font-size: 1.4rem;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.result-floor {
	font-size: 1.2rem;
	color: #888;
	margin-top: 0.2rem;
}

/* Footer with buttons */
.directions-footer {
	padding: 1.6rem 2rem;
	background: #f9f9f9;
	display: flex;
	gap: 1rem;
}

.show-route-btn {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.8rem;
	
	.wf-icon {
		width: 2rem;
		height: 2rem;
	}
	
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
}

.clear-route-btn {
	padding: 1rem 1.6rem;
}

/* Virtual Keyboard */
.directions-keyboard {
	padding: 1.2rem;
	background: #f5f5f5;
	border-top: 1px solid #e0e0e0;
}

.keyboard-row {
	display: flex;
	justify-content: center;
	margin-bottom: 0.6rem;
	
	&:last-child {
		margin-bottom: 0;
	}
}

.keyboard-row-bottom {
	margin-top: 0.4rem;
}

.keyboard-key {
	min-width: 3.2rem;
	height: 3.6rem;
	margin: 0.2rem;
	border: 2px solid var(--th-primary);
	border-radius: 0.4rem;
	background: white;
	font-size: 1.4rem;
	font-weight: 600;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s;
	
	&:hover {
		background: #f0f0f0;
	}
	
	&:active {
		background: var(--th-primary);
		color: white;
	}
}

.key-backspace {
	min-width: 5rem;
	font-size: 1.6rem;
}

.key-space {
	flex: 1;
	max-width: 16rem;
}

.key-clear {
	min-width: 5rem;
	color: #E53935;
	border-color: #E53935;
	
	&:active {
		background: #E53935;
		color: white;
	}
}

/* Mobile / Portrait responsive adjustments */
@media (max-width: 480px), (orientation: portrait) {
	.directions-panel {
		top: 1.5rem;
		left: 1.5rem;
		right: 1.5rem;
		font-size: 2rem;
	}
	
	.directions-content {
		width: 100%;
	}
	
	.directions-toggle {
		padding: 1.8rem 2.4rem;
		
		.wf-icon {
			width: 3rem;
			height: 3rem;
		}
		
		span, label {
			font-size: 2rem;
		}
	}
	
	.directions-header {
		padding: 2rem 2.4rem;
		
		.header-title {
			font-size: 2.4rem;
		}
		
		.close-btn {
			width: 4rem;
			height: 4rem;
			font-size: 3rem;
		}
	}
	
	.directions-body {
		padding: 2.4rem;
	}
	
	.location-input {
		margin-bottom: 1.6rem;
	}
	
	.input-icon {
		width: 4rem;
	}
	
	.icon-dot {
		width: 1.6rem;
		height: 1.6rem;
	}
	
	.location-field {
		padding: 1.7rem 4rem 0.8rem 1.8rem;
		font-size: 1.8rem;
		border-radius: 0.8rem;
	}
	
	.clear-btn {
		width: 2.6rem;
		height: 2.6rem;
		right: 1rem;
		font-size: 1.8rem;
	}
	
	.swap-btn {
		width: 4.5rem;
		height: 4.5rem;
		right: 2.4rem;
		top: 6rem;
		
		svg {
			width: 2.2rem;
			height: 2.2rem;
		}
	}
	
	.search-results {
		max-height: 14rem;
	}
	
	.search-result-item {
		padding: 1.6rem 1.8rem;
	}
	
	.result-icon {
		width: 4rem;
		height: 4rem;
		margin-right: 1.6rem;
	}
	
	.result-name {
		font-size: 1.8rem;
	}
	
	.result-floor {
		font-size: 1.4rem;
	}
	
	.directions-footer {
		padding: 2rem 2.4rem;
	}
	
	.show-route-btn {
		padding: 2rem 2.4rem;
		font-size: 2.2rem;
		
		.wf-icon {
			width: 3rem;
			height: 3rem;
		}
		
		label {
			font-size: 2.2rem;
		}
	}
	
	.clear-route-btn {
		padding: 2rem 2.4rem;
		font-size: 2.2rem;
	}
	
	/* Keyboard portrait - bigger keys */
	.directions-keyboard {
		padding: 1.6rem;
	}
	
	.keyboard-key {
		min-width: 4.2rem;
		height: 4.8rem;
		margin: 0.3rem;
		font-size: 1.8rem;
	}
	
	.key-backspace,
	.key-clear {
		min-width: 6.5rem;
		font-size: 2rem;
	}
	
	.key-space {
		max-width: 20rem;
	}
}

/* Landscape adjustments */
@media (orientation: landscape) {
	.directions-panel {
		top: 1.5rem;
		left: 1.5rem;
	}
	
	.directions-content {
		width: 32rem;
	}
	
	.directions-body {
		padding: 1.5rem;
	}
	
	.location-field {
		padding: 1.2rem 3rem 1.7rem 1.2rem;
		font-size: 1.3rem;
	}
	
	.search-results {
		max-height: 10rem;
	}
	
	.directions-footer {
		padding: 1.2rem 1.5rem;
	}
	
	/* Keyboard landscape */
	.directions-keyboard {
		padding: 1rem;
	}
	
	.keyboard-key {
		min-width: 2.6rem;
		height: 3rem;
		margin: 0.15rem;
		font-size: 1.2rem;
	}
	
	.key-backspace,
	.key-clear {
		min-width: 4.5rem;
		font-size: 1.4rem;
	}
	
	.key-space {
		max-width: 12rem;
	}
}
</style>